import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import MistakesTable from '../Components/MistakesTable';
import AttemptPatternPieChartCard from '../Components/AttemptPatternPieChartCard';
import MistakesLineChart from '../Components/MistakesLineChart';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import { cn } from '../../lib/utils';
import { CardSkeleton, MistakesTableSkeleton, SubjectTabsSkeleton } from '../Components/MistakesLoaders';

const MemoizedHeader = React.memo(() => (
  <div className="mb-6">
    <h2 className="text-2xl font-semibold text-[var(--q3-neutral-default)] mb-2">Chapter-wise Mistakes</h2>
    <p className="text-[var(--q3-neutral-light)] text-xs">
      Revisit questions which you either attempted incorrectly or didn't attempt in the paper
    </p>
  </div>
));

export default function ChapterWiseMistakes() {
  const navigate = useNavigate();
  const [mistakesOverviewExpanded, setMistakesOverviewExpanded] = useState(true);
  const [attemptingPatternExpanded, setAttemptingPatternExpanded] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState(null);
  const [selectedSpecificTests, setSelectedSpecificTests] = useState([]);
  const [isTestDropdownOpen, setIsTestDropdownOpen] = useState(false);
  const [tempSelectedTests, setTempSelectedTests] = useState([]);
  const dropdownRef = useRef(null);
  const [sortBy, setSortBy] = useState('default');
  const [selectedUnit, setSelectedUnit] = useState('All Units');
  const [selectedSubject, setSelectedSubject] = useState({ id: '', title: '', color: 'var(--q3-neutral-light)' });
  const [subjectsData, setSubjectsData] = useState([]);
  const [chapterWiseOverviewData, setChapterWiseOverviewData] = useState([]);
  const [overviewCurrentPage, setOverviewCurrentPage] = useState(1);
  const [overviewTotalPages, setOverviewTotalPages] = useState(1);
  const OVERVIEW_LIMIT = 10;
  const [testTypeOptionsData, setTestTypeOptionsData] = useState([]);
  const [specificTestOptionsData, setSpecificTestOptionsData] = useState([]);
  const [chapterAttemptOverviewPieData, setChapterAttemptOverviewPieData] = useState([]);
  const [unitOptionsData, setUnitOptionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areSubjectsLoading, setAreSubjectsLoading] = useState(true);
  const [isUnitSelectOpen, setIsUnitSelectOpen] = useState(false);
  const [isTestTypeSelectOpen, setIsTestTypeSelectOpen] = useState(false);

  const selectedPackId = localStorage.getItem('selectedPackId');
  const user = JSON.parse(localStorage.getItem('userProfile'));

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTestDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  // 'All Tests' and null both mean "no testType filter" for query purposes, so
  // normalize to null here — otherwise setSelectedTestType('All Tests') below,
  // triggered by a response, would re-run this effect with an identical query.
  const effectiveTestType = selectedTestType && selectedTestType !== 'All Tests' ? selectedTestType : null;

  useEffect(() => {
    if (selectedPackId && selectedSubject.id) {
      setLoading(true);
      const queryParams = new URLSearchParams({
        offset: (overviewCurrentPage - 1) * OVERVIEW_LIMIT,
        limit: OVERVIEW_LIMIT,
        subject: selectedSubject.id,
      });

      if (effectiveTestType) queryParams.append('testType', effectiveTestType);
      if (selectedUnit && selectedUnit !== 'All Units') queryParams.append('unit', selectedUnit);
      if (selectedSpecificTests.length > 0) {
        selectedSpecificTests.forEach((testId) => queryParams.append('selectSpecificTest', testId));
      }

      axios
        .get(`/api/v2/answers/${selectedPackId}/chapter-wise`, { params: queryParams })
        .then((response) => {
          if (response.data.success && response.data.data) {
            const {
              chapters = [],
              testTypeFilterValues = [],
              specificTestFilters = [],
              attemptOverview = [],
              unitFilters = [],
            } = response.data.data;
            setChapterWiseOverviewData(chapters);
            setOverviewTotalPages(response.data.totalPages || 1);
            setTestTypeOptionsData(['All Tests', ...testTypeFilterValues]);
            setSpecificTestOptionsData(specificTestFilters);
            setChapterAttemptOverviewPieData(attemptOverview);
            setUnitOptionsData(unitFilters);
            if (selectedTestType === null && testTypeFilterValues.length > 0) setSelectedTestType('All Tests');
          } else {
            setChapterWiseOverviewData([]);
            setOverviewTotalPages(1);
          }
        })
        .catch((error) => {
          console.error('Error fetching chapter-wise mistakes:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedPackId, effectiveTestType, selectedSpecificTests, selectedSubject, selectedUnit, overviewCurrentPage]);

  useEffect(() => {
    if (selectedPackId) {
      setAreSubjectsLoading(true);
      axios
        .get(`/api/packs/${selectedPackId}/subjects`)
        .then((response) => {
          if (response.data.success && Array.isArray(response.data.data)) {
            setSubjectsData(response.data.data);
            if (response.data.data.length > 0 && !selectedSubject.id) {
              const firstSubject = response.data.data[0];
              setSelectedSubject({ id: firstSubject._id, title: firstSubject.title, color: firstSubject.color });
            }
          }
        })
        .catch((error) => console.error('Error fetching subjects:', error))
        .finally(() => setAreSubjectsLoading(false));
    }
  }, [selectedPackId]);

  const handleSubjectTabChange = useCallback((subjectObj) => {
    if (subjectObj) {
      setSelectedSubject({ id: subjectObj._id, title: subjectObj.title, color: subjectObj.color });
      setSelectedUnit('All Units');
      setOverviewCurrentPage(1);
    }
  }, []);

  const handleChapterClick = (chapter) => {
    window.webengage.track('Chapter Mistakes Opened', {
      'User Id': user?.email,
      'Subject Title': selectedSubject.title,
      'Subject Id': selectedSubject.id,
      'Chapter Id': chapter.chapterId,
      'Chapter Title': chapter.chapterTitle,
    });

    navigate(`/v3/my-mistakes/chapter/${chapter.chapterId}`, {
      state: { chapterTitle: chapter.chapterTitle, specificTestOptions: specificTestOptionsData },
    });
  };

  const subjectTabs = useMemo(
    () => (
      <div className="overflow-x-auto">
        <TabsContainer color={selectedSubject.color} className="mb-6">
          {subjectsData.map((subject) => {
            const isActive = selectedSubject.title === subject.title;
            return (
              <Tab
                key={subject._id}
                isActive={isActive}
                color={isActive ? subject.color : 'var(--q3-neutral-light)'}
                tabColor={subject.color || 'var(--q3-neutral-light)'}
                onClick={() => handleSubjectTabChange(subject)}
              >
                {subject.icon && <img src={subject.icon} alt={subject.title} className="h-5 w-5" />}
                <p className="mb-0">{subject.title}</p>
              </Tab>
            );
          })}
        </TabsContainer>
      </div>
    ),
    [subjectsData, selectedSubject, handleSubjectTabChange],
  );

  const handleUnitChange = (unit) => {
    setSelectedUnit(unit);
    setOverviewCurrentPage(1);
  };
  const handleTestTypeChange = (testType) => {
    setSelectedTestType(testType);
    setOverviewCurrentPage(1);
  };
  const handleApplyTests = () => {
    setSelectedSpecificTests(tempSelectedTests);
    setIsTestDropdownOpen(false);
    setOverviewCurrentPage(1);
  };
  const handleClearTests = () => {
    setTempSelectedTests([]);
    setSelectedSpecificTests([]);
    setIsTestDropdownOpen(false);
    setOverviewCurrentPage(1);
  };
  const toggleDropdown = () => {
    if (!isTestDropdownOpen) {
      setTempSelectedTests(selectedSpecificTests);
    }
    setIsTestDropdownOpen(!isTestDropdownOpen);
  };

  const lineChartLines = [
    { dataKey: 'incorrect', label: 'Incorrect Qs', color: 'var(--q3-danger-normal)' },
    { dataKey: 'correct', label: 'Correct Qs', color: 'var(--q3-success-normal)' },
    { dataKey: 'notAttempted', label: 'Not attempted Qs', color: 'var(--q3-neutral-light)' },
  ];
  const lineChartData = chapterWiseOverviewData.map((chapter) => ({
    name: chapter.chapterTitle,
    incorrect: chapter.totalIncorrect,
    correct: chapter.totalCorrect,
    notAttempted: chapter.totalUnattempted,
  }));

  return (
    <div className="w-full max-w-none overflow-hidden">
      <MemoizedHeader />

      {areSubjectsLoading ? <SubjectTabsSkeleton /> : subjectTabs}

      <div className="flex items-center flex-wrap gap-2 mb-6">
        <Select value={selectedUnit} onValueChange={handleUnitChange} onOpenChange={setIsUnitSelectOpen}>
          <SelectTrigger className={`cursor-pointer w-auto rounded-full`}>
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All Units">All Units</SelectItem>
            {unitOptionsData.map((unit) => (
              <SelectItem key={unit._id} value={unit._id}>
                {unit.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />
        <Select
          value={selectedTestType || ''}
          onValueChange={handleTestTypeChange}
          onOpenChange={setIsTestTypeSelectOpen}
        >
          <SelectTrigger className={cn(`cursor-pointer w-auto rounded-full`)}>
            <SelectValue placeholder="Test Type" />
          </SelectTrigger>
          <SelectContent>
            {testTypeOptionsData.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative" ref={dropdownRef}>
          <Button
            variant="outline"
            onClick={toggleDropdown}
            className={`w-auto justify-between rounded-full ${
              selectedSpecificTests.length > 0
                ? ' border-2 border-[var(--q3-accent-normal)] bg-[var(--q3-accent-light)] text-[var(--q3-accent-normal)]'
                : 'border-input'
            }`}
          >
            {selectedSpecificTests.length > 0
              ? `${selectedSpecificTests.length} test(s) selected`
              : 'Select Specific Test'}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
          {isTestDropdownOpen && (
            <div className="absolute z-10 top-full mt-2 min-w-[280px] bg-card border rounded-md shadow-lg max-w-[340px] md:max-w-[500px] lg:max-w-[380px] xl:max-w-none">
              <div className="flex justify-between items-center p-2">
                <p className="text-xs font-semibold">Select Specific Tests</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleClearTests}
                    className="text-sm cursor-pointer text-[var(--q3-danger-normal)] hover:underline"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleApplyTests}
                    className="text-sm cursor-pointer text-[var(--q3-accent-normal)] hover:underline"
                  >
                    Apply
                  </button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto p-1">
                {specificTestOptionsData.length > 0 ? (
                  specificTestOptionsData.map((test) => {
                    const isSelected = tempSelectedTests.includes(test._id);
                    return (
                      <label key={test._id} className="flex items-center p-2 rounded-md hover:bg-accent cursor-pointer">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            const newSelection = checked
                              ? [...tempSelectedTests, test._id]
                              : tempSelectedTests.filter((id) => id !== test._id);
                            setTempSelectedTests(newSelection);
                          }}
                          className="mr-2 data-[state=checked]:border-[var(--q3-accent-normal)] data-[state=checked]:bg-[var(--q3-accent-normal)] data-[state=checked]:text-[var(--q3-neutral-default_inverted)]"
                        />
                        <span className="text-sm w-full whitespace-nowrap">{test.title}</span>
                      </label>
                    );
                  })
                ) : (
                  <p className="p-2 text-sm text-muted-foreground">No tests found.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <>
          <CardSkeleton />
          <CardSkeleton />
          <MistakesTableSkeleton />
        </>
      ) : (
        <>
          <MistakesLineChart
            title="Your Mistakes Overview"
            description="This graph shows how you performed across different chapters."
            data={lineChartData}
            expanded={mistakesOverviewExpanded}
            onToggleExpand={() => setMistakesOverviewExpanded(!mistakesOverviewExpanded)}
            lines={lineChartLines}
            xAxisDataKey="name"
          />
          <AttemptPatternPieChartCard
            data={chapterAttemptOverviewPieData}
            expanded={attemptingPatternExpanded}
            onToggleExpand={() => setAttemptingPatternExpanded(!attemptingPatternExpanded)}
          />
          <MistakesTable
            title="Chapter-wise Mistakes"
            sortBy={sortBy}
            onSortByChange={setSortBy}
            data={chapterWiseOverviewData}
            onRowClick={handleChapterClick}
            nameColumnTitle="Chapter Name"
            currentPage={overviewCurrentPage}
            totalPages={overviewTotalPages}
            onPageChange={setOverviewCurrentPage}
          />
        </>
      )}
    </div>
  );
}
